import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ArrowRight, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { StatusBadge } from '@/components/custom/StatusBadge';
import { TypeBadge } from '@/components/custom/TypeBadge';
import { quickSearchTickets } from '@/hooks/useTickets';
import type { TicketSearchItem } from '@uniticket/shared';

interface GlobalSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GlobalSearchDialog({ open, onOpenChange }: GlobalSearchDialogProps) {
  const { t } = useTranslation('tickets');
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<TicketSearchItem[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setQuery('');
      setResults([]);
      setLoading(false);
    }
  }, [open]);

  const doSearch = useCallback((q: string) => {
    if (!q.trim()) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    quickSearchTickets(q.trim())
      .then((res) => setResults(res.data))
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  }, []);

  function handleQueryChange(value: string) {
    setQuery(value);
    clearTimeout(debounceRef.current);
    if (!value.trim()) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(() => doSearch(value), 300);
  }

  function handleSelect(ticketId: string) {
    onOpenChange(false);
    navigate(`/tickets/${ticketId}`);
  }

  function handleViewAll() {
    if (!query.trim()) return;
    onOpenChange(false);
    navigate(`/tickets?q=${encodeURIComponent(query.trim())}`);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 gap-0 max-w-xl overflow-hidden" aria-describedby={undefined}>
        {/* Search input */}
        <div className="flex items-center border-b border-slate-200 px-3">
          <Search className="mr-2 h-4 w-4 shrink-0 text-slate-400" />
          <input
            autoFocus
            type="text"
            placeholder={t('search.placeholder')}
            value={query}
            onChange={(e) => handleQueryChange(e.target.value)}
            className="flex h-11 w-full bg-transparent py-3 text-sm outline-none placeholder:text-slate-400"
          />
        </div>

        {/* Results */}
        <div className="max-h-80 overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
            </div>
          )}

          {!loading && query.trim() && results.length === 0 && (
            <p className="py-6 text-center text-sm text-slate-500">{t('search.noResults')}</p>
          )}

          {!loading && results.length > 0 && (
            <div className="p-1">
              <p className="px-2 py-1.5 text-xs font-medium text-slate-500">{t('search.tickets')}</p>
              {results.map((ticket) => (
                <button
                  key={ticket.id}
                  type="button"
                  onClick={() => handleSelect(ticket.id)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md cursor-pointer text-left hover:bg-slate-100 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-mono font-semibold text-brand-rose text-xs">
                        {ticket.ticketNumber}
                      </span>
                      {ticket.jvid && (
                        <span className="text-[10px] font-mono text-slate-400">
                          {t('search.jvid', { jvid: ticket.jvid })}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-700 truncate">{ticket.subject}</p>
                    {ticket.description && (
                      <p className="text-xs text-slate-400 truncate mt-0.5">{ticket.description.substring(0, 100)}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <TypeBadge type={ticket.ticketType} />
                    <StatusBadge status={ticket.status} />
                  </div>
                </button>
              ))}
            </div>
          )}

          {!loading && query.trim() && results.length > 0 && (
            <div className="border-t border-slate-100 p-2">
              <button
                type="button"
                onClick={handleViewAll}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium text-brand-rose hover:bg-brand-rose/5 rounded-md transition-colors"
              >
                {t('search.viewAll', { query: query.trim() })}
                <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
